import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AdminActions } from "src/redux/Admin/actions";
import { AdminSelectors } from "src/redux/Admin/selectors";
import { ConfirmActions } from "src/redux/Confirm/actions";
import Button from "src/components/common/Button";
import CheckBox from "src/components/common/CheckBox";
import TagInput from "./TagInput";
import useUnsavedChangesGuard from "src/utils/useUnsavedChangesGuard";
import styles from "./styles.module.scss";

const CptyManagement = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const cpties = useSelector(AdminSelectors.Cpties);
    const loading = useSelector(AdminSelectors.Loading);
    const error = useSelector(AdminSelectors.Error);
    const purgeGraceSecs = useSelector((state) => state.Admin.purgeGraceSecs);

    const [pendingRemovals, setPendingRemovals] = useState(new Set());
    // Hide cpties scheduled for deletion (greyed rows). Operator preference,
    // remembered across sessions.
    const [hideScheduled, setHideScheduled] = useState(
        () => localStorage.getItem("hideScheduledCpties") === "true"
    );
    const [hideTest, setHideTest] = useState(
        () => localStorage.getItem("hideTestCpties") === "true"
    );
    // Cpties whose removal was JUST confirmed: shown disabled immediately,
    // with a locally computed deletion deadline (now + grace), before the
    // backend's lifecycle flip and purgeAt reach the next list fetch.
    const [scheduledLocally, setScheduledLocally] = useState(new Map());

    useEffect(() => {
        dispatch(AdminActions.FetchCpties());
    }, [dispatch]);

    useEffect(() => {
        localStorage.setItem("hideScheduledCpties", String(hideScheduled));
    }, [hideScheduled]);

    useEffect(() => {
        localStorage.setItem("hideTestCpties", String(hideTest));
    }, [hideTest]);

    // Sort cpties by code
    const sortedCpties = useMemo(() => {
        return [...cpties].sort((a, b) => (a.cptyCode || "").localeCompare(b.cptyCode || ""));
    }, [cpties]);

    // Any navigation away (Manage, the nav tabs, browser close) with
    // pending removals goes through the standard Unsaved Changes dialog.
    useUnsavedChangesGuard(
        pendingRemovals.size > 0,
        [...pendingRemovals].map((code) => `Remove ${code}`).join("\n")
    );

    const handleManage = useCallback((cptyCode) => {
        history.push(`/cpties/${cptyCode}`);
    }, [history]);

    const togglePendingRemoval = (cptyCode) => {
        setPendingRemovals((prev) => {
            const next = new Set(prev);
            if (next.has(cptyCode)) next.delete(cptyCode);
            else next.add(cptyCode);
            return next;
        });
    };

    // A cpty in its off-board grace window: disabled, deletion scheduled,
    // cancellable until the purge claims it. A just-confirmed removal counts
    // immediately (scheduledLocally), ahead of the backend's lifecycle flip.
    const isOffboarding = (c) =>
        c.lifecycle === "OFF_BOARDING" || scheduledLocally.has(c.cptyCode);

    const purgeAtOf = (c) => c.purgeAt ?? scheduledLocally.get(c.cptyCode);

    const deletionLabel = (c) => {
        const purgeAt = purgeAtOf(c);
        if (!purgeAt) return "Access disabled. Scheduled for deletion.";
        const msLeft = Math.max(0, purgeAt - Date.now());
        const hours = msLeft / 3600000;
        const when = hours >= 1
            ? `${Math.ceil(hours)} hour${Math.ceil(hours) > 1 ? "s" : ""}`
            : `${Math.max(1, Math.ceil(msLeft / 60000))} min`;
        return `Access disabled. Scheduled for deletion in ${when}.`;
    };

    const handleCancelDeletion = (cptyCode) => {
        dispatch(ConfirmActions.Open({
            title: "Cancel Scheduled Deletion",
            message: `Cancel the scheduled deletion of ${cptyCode}?\nAccess is restored and the counterparty becomes active again.`,
            getDescription: true,
            isDescriptionRequired: true,
            descriptionPlaceholder: "Reason for cancelling the deletion",
            okTitle: "Cancel Deletion",
            cancelTitle: "Keep Scheduled",
            onOk: async () => {
                try {
                    await dispatch(AdminActions.CancelOffboard(cptyCode));
                    setScheduledLocally((prev) => {
                        const next = new Map(prev);
                        next.delete(cptyCode);
                        return next;
                    });
                    // The lifecycle flips asynchronously; refresh until it lands.
                    for (const delay of [1500, 4000, 8000]) {
                        await new Promise((r) => setTimeout(r, delay));
                        dispatch(AdminActions.FetchCpties());
                    }
                } catch (e) {
                    console.error("Failed to cancel deletion of", cptyCode, e);
                }
            },
        }));
    };

    const handleConfirmRemovals = () => {
        const codes = [...pendingRemovals];
        dispatch(ConfirmActions.Open({
            title: "Remove Counterparties",
            message: `Remove ${codes.length} counterpart${codes.length > 1 ? "ies" : "y"}:\n${codes.join("\n")}`,
            subMessage: "This removes their access and takes them out of the directory. Their trade data is retained (queryable by code) unless you also delete it below.",
            getDescription: true,
            isDescriptionRequired: true,
            descriptionPlaceholder: "Remove X due to Y",
            checkboxLabel: "Also delete their data (projections)",
            checkboxNote: "The data is archived to S3 (backed up) before deletion, so it can be restored later.",
            okTitle: "Remove",
            cancelTitle: "Cancel",
            warning: true,
            onOk: async (comment, deleteData) => {
                for (const c of codes) {
                    try {
                        await dispatch(AdminActions.OffboardCpty(c, comment, deleteData));
                    } catch (e) {
                        console.error("Failed to offboard", c, e);
                    }
                }
                setPendingRemovals(new Set());
                setScheduledLocally((prev) => {
                    const next = new Map(prev);
                    const purgeAt = purgeGraceSecs
                        ? Date.now() + purgeGraceSecs * 1000
                        : undefined;
                    codes.forEach((c) => next.set(c, purgeAt));
                    return next;
                });
            },
        }));
    };

    // `testCpty` is set when the e2e suite creates a counterparty, so hiding
    // them leaves the real ones, which on a well-used cluster is a handful
    // among hundreds.
    const visibleCpties = sortedCpties
        .filter((c) => !(hideScheduled && isOffboarding(c)))
        .filter((c) => !(hideTest && c.testCpty));
    const hiddenScheduledCount = hideScheduled
        ? sortedCpties.filter(isOffboarding).length
        : 0;
    const hiddenTestCount = hideTest
        ? sortedCpties.filter((c) => c.testCpty).length
        : 0;

    return (
        <div className={`${styles.cptyManagement} ${pendingRemovals.size > 0 ? styles.hasStatusBar : ""}`}>
            <div className={styles.header}>
                <div className={styles.title}>Counterparty Management</div>
                <Button
                    value="Onboard New Counterparty"
                    dataTestId={"onboard-new-counterparty"}
                    onClick={() => history.push("/cpties/new")}
                    classes={[styles.primaryBtn]}
                />
            </div>

            {error && <div className={styles.error}>{error}</div>}
            {loading && <div className={styles.loading}>Loading...</div>}

            {!loading && sortedCpties.length === 0 && (
                <div className={styles.empty}>No counterparties configured</div>
            )}

            {sortedCpties.length > 0 && (
                <div className={styles.hideScheduledRow}>
                    <CheckBox
                        name="hideScheduled"
                        selected={hideScheduled}
                        onChange={(e) => setHideScheduled(e.target.checked)}
                        small
                        styleName="black"
                        dataTestId="hide-scheduled-toggle"
                    >
                        Hide scheduled for deletion
                        {hiddenScheduledCount > 0 && (
                            <span className={styles.hiddenCount}>({hiddenScheduledCount} hidden)</span>
                        )}
                    </CheckBox>
                    <CheckBox
                        name="hideTest"
                        selected={hideTest}
                        onChange={(e) => setHideTest(e.target.checked)}
                        small
                        styleName="black"
                        dataTestId="hide-test-toggle"
                    >
                        Hide test cpties
                        {hiddenTestCount > 0 && (
                            <span className={styles.hiddenCount}>({hiddenTestCount} hidden)</span>
                        )}
                    </CheckBox>
                </div>
            )}

            {sortedCpties.length > 0 && (
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Code</th>
                            <th>Name</th>
                            <th>Mail Domains</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleCpties.map((cpty) => (
                            <tr key={cpty.cptyCode} className={pendingRemovals.has(cpty.cptyCode) || isOffboarding(cpty) ? styles.pendingRemoval : undefined}>
                                <td>{cpty.cptyCode}</td>
                                <td>{cpty.cptyName}</td>
                                <td><TagInput value={cpty.mailDomains} readOnly /></td>
                                <td>{isOffboarding(cpty) ? deletionLabel(cpty) : "Active"}</td>
                                <td className={styles.actionButtons}>
                                    {/* A scheduled cpty's access/config is disabled;
                                        managing it only produces errors. */}
                                    {!isOffboarding(cpty) && (
                                        <Button
                                            value="Manage"
                                            onClick={() => handleManage(cpty.cptyCode)}
                                            classes={[styles.detailBtn]}
                                            dataTestId={`manage-${cpty.cptyCode}`}
                                        />
                                    )}
                                    {isOffboarding(cpty) ? (
                                        <Button
                                            value="Cancel deletion"
                                            onClick={() => handleCancelDeletion(cpty.cptyCode)}
                                            classes={[styles.detailBtn]}
                                            dataTestId={`cancel-deletion-${cpty.cptyCode}`}
                                        />
                                    ) : (
                                        <Button
                                            value={pendingRemovals.has(cpty.cptyCode) ? "Undo" : "Remove"}
                                            onClick={() => togglePendingRemoval(cpty.cptyCode)}
                                            classes={[pendingRemovals.has(cpty.cptyCode) ? styles.detailBtn : styles.removeBtn]}
                                        />
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* Status bar for pending removals */}
            {pendingRemovals.size > 0 && (
                <div className={styles.statusBar}>
                    <span>{pendingRemovals.size} counterpart{pendingRemovals.size > 1 ? "ies" : "y"} marked for removal</span>
                    <Button
                        value="Discard Changes"
                        onClick={() => setPendingRemovals(new Set())}
                        classes={[styles.detailBtn]}
                    />
                    <Button
                        value="Confirm Removal"
                        onClick={handleConfirmRemovals}
                        classes={[styles.removeBtn]}
                    />
                </div>
            )}
        </div>
    );
};

export default CptyManagement;
